import { Badge } from "@/components/ui/Badge";
import { strings } from "@/lib/strings";
import type { Antibiotic, AstValue } from "@/lib/types";
import { cn } from "@/lib/utils";

const CHOICES: Array<{ value: Exclude<AstValue, null>; label: string; hint: string }> = [
  { value: "S", label: "S", hint: "Susceptible" },
  { value: "I", label: "I", hint: "Intermediate" },
  { value: "R", label: "R", hint: "Resistant" },
];

/**
 * The antibiotic grid (docs/08 §8.3, E-07).
 *
 * "Each drug is one row with three buttons (S, I, R) and a clear option. Tab
 * moves through the drugs; pressing s, i, r or blank sets the value."
 *
 * Each row is a radiogroup, so a screen reader announces the drug and the
 * chosen value, and arrow keys move within the row as users expect.
 */
export function AntibioticGrid({
  antibiotics,
  values,
  onChange,
  disabled,
}: {
  antibiotics: Antibiotic[];
  values: Record<string, AstValue>;
  onChange: (code: string, value: AstValue) => void;
  disabled?: boolean;
}) {
  const handleKey = (event: React.KeyboardEvent, code: string) => {
    const key = event.key.toLowerCase();
    if (key === "s" || key === "i" || key === "r") {
      event.preventDefault();
      onChange(code, key.toUpperCase() as Exclude<AstValue, null>);
    } else if (key === "backspace" || key === "delete" || key === "0") {
      event.preventDefault();
      onChange(code, null);
    }
  };

  return (
    <ul className="divide-y divide-slate-100">
      {antibiotics.map((drug) => {
        const value = values[drug.code] ?? null;
        return (
          <li
            key={drug.code}
            className="flex items-center gap-3 py-2"
            onKeyDown={(event) => handleKey(event, drug.code)}
          >
            <div className="min-w-0 flex-1">
              <span className="text-sm text-slate-800">{drug.name}</span>
              <span className="ml-2 font-mono text-xs text-slate-400">{drug.code}</span>
              {drug.is_tbc && (
                <Badge tone="warning" className="ml-2">
                  to confirm
                </Badge>
              )}
            </div>

            <div
              role="radiogroup"
              aria-label={`${drug.name} result`}
              className="flex shrink-0 items-center gap-1"
            >
              {CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  role="radio"
                  aria-checked={value === choice.value}
                  aria-label={`${drug.name}: ${choice.hint}`}
                  disabled={disabled}
                  // Keeps Tab landing once per row, not once per button.
                  tabIndex={value === choice.value || (!value && choice.value === "S") ? 0 : -1}
                  onClick={() => onChange(drug.code, choice.value)}
                  className={cn(
                    "h-10 w-10 rounded-md border text-sm font-semibold transition-colors",
                    "focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1",
                    value === choice.value
                      ? choice.value === "R"
                        ? "border-red-700 bg-red-700 text-white"
                        : choice.value === "I"
                          ? "border-amber-600 bg-amber-600 text-white"
                          : "border-green-700 bg-green-700 text-white"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50",
                    disabled && "opacity-50",
                  )}
                >
                  {choice.label}
                </button>
              ))}

              <button
                type="button"
                disabled={disabled || value === null}
                onClick={() => onChange(drug.code, null)}
                aria-label={`Clear ${drug.name}`}
                className="h-10 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              >
                {strings.common.notTested}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
